import { IBaseEventDetail } from '@kurtosys/types/eventBus/IBaseEventDetail';
import { WindowManager } from '@kurtosys/window-manager';
/**
 * The EventBusHelper is used to keep track of Kurtosys App Template events on an embedded page.
 *
 * This class is to be implemented within the ksys-app-start & ksys-app-manager and will be use the HTML event system to communicate with other app templates on the page.
 *
 * NOTE: unit tests are not possible within the ksys-app-template for this helper as this class relies on the HTML DOM, which is not available within Jest by default.
 *
 * @export
 * @class EventBusHelper
 */
export class EventBusHelper {
	private bus: Element;
	constructor(public applicationEventBusID: string) {
		// look for our custom web component, otherwise add it to the page if it doesn't exist.
		let eventBus = document.querySelector('ksys-event-bus');
		if (!eventBus) {
			try {
				eventBus = document.createElement('ksys-event-bus');
			} catch (e) {
				// inform the console that an issue occurred here and let the app templates handle the error details.
				console.error('Error creating web component "ksys-event-bus"');
				throw e;
			}
			document.body.appendChild(eventBus);
		}
		this.bus = eventBus;
	}

	/**
	 * Register using Element.addEventListener on the custom web component which is available on the page for the event bus
	 *
	 * @param eventType string
	 * @param listener EventListenerOrEventListenerObject
	 * @param [options] AddEventListenerOptions
	 */
	register(
		eventType: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | AddEventListenerOptions,
	): void {
		if (this.bus) {
			this.bus.addEventListener(eventType, listener, options);
		}
	}

	/**
	 * Unregister using Element.removeEventListener on the custom web component which is available on the page for the event bus
	 *
	 * @param eventType
	 * @param listener
	 * @param [options]
	 */
	unregister(
		eventType: string,
		listener: EventListenerOrEventListenerObject,
		options?: boolean | EventListenerOptions,
	): void {
		if (this.bus) {
			this.bus.removeEventListener(eventType, listener, options);
		}
	}

	/**
	 * Trigger an event which will be consumed by all apps registered to event type linked to the event bus.
	 *
	 * @template T
	 * @param eventType
	 * @param [customEventInit]
	 */
	trigger<T extends IBaseEventDetail>(eventType: string, customEventInit?: CustomEventInit<T>) {
		try {
			// Checks if the eventDetails exist in the WindowManager, and sets it to the existing value else defaults it to a new {}
			const eventDetails = WindowManager.has('eventDetails') ? JSON.parse(WindowManager.get('eventDetails')) : {};
			eventDetails[this.applicationEventBusID] = {
				...eventDetails[this.applicationEventBusID],
				[eventType]: customEventInit,
			};
			WindowManager.set('eventDetails', JSON.stringify(eventDetails));
		} catch (e) {
			console.warn('Failed to parse event bus storage', e.stack);
		}
		if (this.bus) {
			this.bus.dispatchEvent(new CustomEvent(eventType, customEventInit));
		}
	}
}
